import React from "react";
import {
  TrendingUp, Rocket, DollarSign, Repeat, Share2, Trophy,
  Megaphone, ArrowDownRight, Globe, Eye, Filter,
} from "lucide-react";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmtH = (h) => (h == null ? "—" : h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${h}h` : `${Math.round(h / 24)}d`);

function Section({ icon: Icon, title, sub, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`mkt-${title}`}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-emerald-500" /> {title}
        {sub && <span className="font-normal text-muted-foreground">· {sub}</span>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-border bg-background/40 p-3 text-center" data-testid={`mkt-stat-${label}`}>
      <div className={`font-heading text-2xl font-bold ${accent || ""}`}>{value}</div>
      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function BarList({ items = [], empty = "No data yet." }) {
  const max = Math.max(1, ...items.map((i) => i.n));
  if (!items.length) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <div className="space-y-1.5">
      {items.map((it) => (
        <div key={it.name} className="flex items-center gap-2 text-xs">
          <span className="w-20 shrink-0 truncate text-muted-foreground">{it.name}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(it.n / max) * 100}%` }} /></div>
          <span className="w-8 shrink-0 text-right font-semibold">{it.n}</span>
        </div>
      ))}
    </div>
  );
}

export function MarketingPanel({ m }) {
  if (!m) return <p className="py-16 text-center text-sm text-muted-foreground" data-testid="marketing-error">Couldn't load marketing analytics.</p>;
  return (
    <div className="space-y-4" data-testid="marketing-panel">
            {/* Site traffic — in-app tracker */}
            {m.traffic && (
              <Section icon={Globe} title="Site traffic" sub="marketing-site visits (in-app)">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Visits 7d" value={m.traffic.visits_7d} accent="text-emerald-500" />
                  <Stat label="Visits 30d" value={m.traffic.visits_30d} />
                  <Stat label="Unique 7d" value={m.traffic.unique_7d} />
                  <Stat label="Unique 30d" value={m.traffic.unique_30d} />
                </div>
                <div className="mt-4 flex items-end gap-1" style={{ height: 56 }}>
                  {(() => {
                    const max = Math.max(1, ...m.traffic.series);
                    return m.traffic.series.map((v, i) => (
                      <div key={i} className="flex-1 rounded-t bg-emerald-500/70" style={{ height: `${(v / max) * 100}%`, minHeight: v ? 2 : 0 }} title={`${v}`} />
                    ));
                  })()}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">Daily visits · last 30 days</div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground">Top pages</div>
                    <div className="mt-2"><BarList items={m.traffic.top_pages} empty="No visits tracked yet." /></div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground">Top sources</div>
                    <div className="mt-2"><BarList items={m.traffic.top_sources} empty="No sources yet." /></div>
                  </div>
                </div>
                {m.traffic.top_countries?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-muted-foreground">Top countries</div>
                    <div className="mt-2"><BarList items={m.traffic.top_countries} empty="No country data yet." /></div>
                  </div>
                )}
                <p className="mt-3 text-[11px] text-muted-foreground">Counts real human visits to your public pages (bots filtered). Works even without HeyCatch.</p>
              </Section>
            )}

            {/* Landing funnel — anonymous mid-funnel drop-off (scroll → demo → pricing → CTA → signup) */}
            {m.landing_funnel && (
              <Section icon={Filter} title="Landing funnel" sub="where visitors drop before signup">
                {m.landing_funnel.visitors === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="landing-funnel-empty">No landing-page events tracked yet — this fills in as visitors hit your public pages.</p>
                ) : (
                  <div className="space-y-2" data-testid="landing-funnel">
                    {m.landing_funnel.stages.map((s) => {
                      const top = m.landing_funnel.stages[0].count || 1;
                      const pct = Math.round((s.count / top) * 100);
                      return (
                        <div key={s.stage} className="flex items-center gap-2 text-xs" data-testid={`landing-stage-${s.stage}`}>
                          <span className="w-28 shrink-0 truncate font-medium">{s.label}</span>
                          <div className="h-6 flex-1 overflow-hidden rounded-md bg-muted">
                            <div className="flex h-full items-center justify-end rounded-md bg-emerald-500 px-2 text-[11px] font-bold text-white" style={{ width: `${Math.max(pct, 10)}%` }}>{pct}%</div>
                          </div>
                          <span className="w-8 shrink-0 text-right font-semibold">{s.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-3 text-[11px] text-muted-foreground">Anonymous visitors moving down your landing page — the big drop shows exactly where to tighten copy or the CTA. Signups shown are last-30-day.</p>
              </Section>
            )}

            {/* Live sample engagement — public demo report opens (kept out of the job Activity feed) */}
            {m.demo && (
              <Section icon={Eye} title="Live sample engagement" sub="public demo report opens">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Total opens" value={m.demo.opens_total} accent="text-emerald-500" />
                  <Stat label="Opens 7d" value={m.demo.opens_7d} />
                  <Stat label="People" value={m.demo.people} />
                  <Stat label="Views logged" value={m.demo.views_tracked} />
                </div>
                <div className="mt-4 flex items-end gap-1" style={{ height: 56 }}>
                  {(() => {
                    const max = Math.max(1, ...m.demo.series);
                    return m.demo.series.map((v, i) => (
                      <div key={i} className="flex-1 rounded-t bg-emerald-500/70" style={{ height: `${(v / max) * 100}%`, minHeight: v ? 2 : 0 }} title={`${v}`} />
                    ));
                  })()}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">Daily sample opens · last 30 days</div>
                {m.demo.top_countries?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-muted-foreground">Top countries</div>
                    <div className="mt-2"><BarList items={m.demo.top_countries} empty="No country data yet." /></div>
                  </div>
                )}
                <p className="mt-3 text-[11px] text-muted-foreground">Strangers opening your public sample link — moved here so it stays out of your job Activity feed.</p>
              </Section>
            )}

            {/* Growth */}
            <Section icon={TrendingUp} title="Growth" sub="signups, week over week">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="This week" value={m.acquisition.growth.this_week} />
                <Stat label="Last week" value={m.acquisition.growth.last_week} />
                <Stat
                  label="WoW"
                  value={m.acquisition.growth.wow_pct == null ? "—" : `${m.acquisition.growth.wow_pct > 0 ? "+" : ""}${m.acquisition.growth.wow_pct}%`}
                  accent={m.acquisition.growth.wow_pct == null ? "" : m.acquisition.growth.wow_pct >= 0 ? "text-emerald-500" : "text-rose-500"}
                />
              </div>
              <div className="mt-4 text-xs font-semibold text-muted-foreground">Signups by day of week</div>
              <div className="mt-2 flex items-end gap-1.5" style={{ height: 64 }}>
                {(() => {
                  const max = Math.max(1, ...m.acquisition.dow);
                  return m.acquisition.dow.map((v, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center gap-1">
                      <div className="flex w-full flex-1 items-end"><div className="w-full rounded-t bg-emerald-500/80" style={{ height: `${(v / max) * 100}%`, minHeight: v ? 3 : 0 }} title={`${v}`} /></div>
                      <span className="text-[10px] text-muted-foreground">{DOW[i]}</span>
                    </div>
                  ));
                })()}
              </div>
            </Section>

            {/* Acquisition */}
            <Section icon={Megaphone} title="Acquisition" sub="where signups come from">
              <BarList items={m.acquisition.sources} empty="No attribution yet — add ?utm_source=… to your links." />
              <p className="mt-3 text-[11px] text-muted-foreground">Tag your ads/posts with <code className="rounded bg-muted px-1">?utm_source=facebook</code> and they'll show here.</p>
            </Section>

            {/* Activation */}
            <Section icon={Rocket} title="Activation" sub="from signup to first value">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Activation" value={`${m.activation.activation_rate}%`} sub={`${m.activation.activated}/${m.activation.signups}`} accent="text-emerald-500" />
                <Stat label="→ 1st link" value={fmtH(m.activation.median_ttf_link_h)} sub="median" />
                <Stat label="→ 1st proof" value={fmtH(m.activation.median_ttf_proof_h)} sub="median" />
                <Stat label="Signups" value={m.activation.signups} sub="logged in" />
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">Activated = sent their first link within 7 days of signing up.</p>
            </Section>

            {/* Activation funnel — explicit milestone tracking with time-to-each */}
            {m.activation_funnel && (
              <Section icon={Filter} title="Activation funnel" sub="signup → first proof, time-to-each">
                {m.activation_funnel.tracked === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="funnel-empty">No new signups tracked yet — this fills in as new users move through the app.</p>
                ) : (
                  <div className="space-y-0" data-testid="activation-funnel">
                    {m.activation_funnel.stages.map((s, i) => {
                      const top = m.activation_funnel.stages[0].count || 1;
                      const pct = Math.round((s.count / top) * 100);
                      const gap = i > 0 ? m.activation_funnel.gaps[i - 1] : null;
                      return (
                        <div key={s.stage} data-testid={`funnel-stage-${s.stage}`}>
                          {gap && (
                            <div className="flex items-center gap-1.5 py-1 pl-[7.5rem] text-[11px] text-muted-foreground">
                              <ArrowDownRight className="h-3 w-3" />
                              <span>{fmtH(gap.median_h)} median · {gap.n} user{gap.n === 1 ? "" : "s"}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs">
                            <span className="w-28 shrink-0 truncate font-medium">{s.label}</span>
                            <div className="h-6 flex-1 overflow-hidden rounded-md bg-muted">
                              <div className="flex h-full items-center justify-end rounded-md bg-emerald-500 px-2 text-[11px] font-bold text-white" style={{ width: `${Math.max(pct, 12)}%` }}>{pct}%</div>
                            </div>
                            <span className="w-8 shrink-0 text-right font-semibold">{s.count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-3 text-[11px] text-muted-foreground">Each user's first time reaching every stage, plus the median time between stages. Sample starter jobs are excluded.</p>
              </Section>
            )}

            {/* Conversion */}
            <Section icon={DollarSign} title="Conversion &amp; revenue">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Trial→Paid" value={`${m.conversion.trial_to_paid_pct}%`} accent="text-emerald-500" />
                <Stat label="Active" value={m.conversion.active_subs} sub="subs" />
                <Stat label="On trial" value={m.conversion.trialing_now} />
                <Stat label="Churn" value={`${m.conversion.churn_pct}%`} accent={m.conversion.churn_pct > 0 ? "text-rose-500" : ""} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-xl bg-emerald-500/10 px-4 py-2 text-sm">
                  <span className="text-muted-foreground">Est. MRR</span>
                  <span className="font-heading text-lg font-bold text-emerald-500">${m.conversion.est_mrr}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-2 text-sm">
                  <span className="text-muted-foreground">ARPU</span>
                  <span className="font-heading text-lg font-bold">${m.conversion.arpu}</span>
                </div>
              </div>
            </Section>

            {/* Retention */}
            <Section icon={Repeat} title="Retention" sub="% of signups active each week">
              <div className="flex items-end gap-1.5" style={{ height: 90 }}>
                {m.retention.map((r) => (
                  <div key={r.week} className="flex flex-1 flex-col items-center gap-1">
                    <span className="text-[10px] font-semibold text-muted-foreground">{r.pct}%</span>
                    <div className="flex w-full flex-1 items-end"><div className="w-full rounded-t bg-emerald-500" style={{ height: `${r.pct}%`, minHeight: r.pct ? 3 : 0, opacity: 0.4 + 0.6 * (r.pct / 100) }} title={`${r.active}/${r.eligible}`} /></div>
                    <span className="text-[10px] text-muted-foreground">W{r.week}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">W0 is signup week. Higher later weeks = stickier product.</p>
            </Section>

            {/* Virality */}
            <Section icon={Share2} title="Virality &amp; reach" sub="every link spreads your brand">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total opens" value={m.virality.total_opens} />
                <Stat label="Links sent" value={m.virality.links_sent} />
                <Stat label="Opens/link" value={m.virality.avg_opens_per_link} accent="text-emerald-500" />
              </div>
              {m.virality.top.length > 0 && (
                <>
                  <div className="mt-4 text-xs font-semibold text-muted-foreground">Furthest-reaching reports</div>
                  <div className="mt-2"><BarList items={m.virality.top.map((t) => ({ name: t.title, n: t.opens }))} /></div>
                </>
              )}
            </Section>

            {/* Power users */}
            <Section icon={Trophy} title="Power users" sub="most links sent · last 30 days">
              {m.power_users.length === 0 ? (
                <p className="text-xs text-muted-foreground">No active senders in the last 30 days.</p>
              ) : (
                <div className="space-y-1.5">
                  {m.power_users.map((p, i) => (
                    <div key={p.email} className="flex items-center justify-between text-sm" data-testid={`power-user-${p.email}`}>
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-[10px] font-bold text-emerald-500">{i + 1}</span>
                        <span className="truncate">{p.email}</span>
                      </span>
                      <span className="shrink-0 font-semibold">{p.links_30d} links</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
  );
}
